"use client";

import { useProductStore } from "@/components/context/product-store";
import InputGrayStart from "@/components/core/input-gray-start";
import { IOfferzBuyOptions } from "@/types/product";
import { magicConstants } from "@/utils/magic-constants";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import React, { useEffect, useMemo } from "react";
import { useShallow } from "zustand/shallow";

interface OwnAmountInputProps {
  buyOptions: IOfferzBuyOptions[];
}

const OwnAmountInput: React.FC<OwnAmountInputProps> = ({ buyOptions }) => {
  const { i18n } = useLingui();

  const {
    ownAmount,
    setOwnAmount,
    minAmount,
    maxAmount,
    setError,
    helperText,
    setHelperText,
    setInputValues,
    setMinAmount,
    setMaxAmount,
    apiErrors,
  } = useProductStore(
    useShallow((state) => ({
      ownAmount: state.ownAmount,
      setOwnAmount: state.setOwnAmount,
      minAmount: state.minAmount,
      maxAmount: state.maxAmount,
      error: state.error,
      setError: state.setError,
      helperText: state.helperText,
      setHelperText: state.setHelperText,
      setInputValues: state.setInputValues,
      setMinAmount: state.setMinAmount,
      setMaxAmount: state.setMaxAmount,
      apiErrors: state.apiErrors,
    })),
  );

  const ownAmountOption = useMemo(
    () =>
      buyOptions?.find(
        (option: IOfferzBuyOptions) => option.option_id === magicConstants.priceOptionID,
      ),
    [buyOptions],
  );

  useEffect(() => {
    if (ownAmountOption?.conditions) {
      const min = ownAmountOption.conditions.min_amount ?? 1;
      const max = ownAmountOption.conditions.max_amount ?? Infinity;

      setMinAmount(min);
      setMaxAmount(max);
    }
  }, [ownAmountOption, setMinAmount, setMaxAmount]);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const numericValue = parseFloat(value);
    setOwnAmount(numericValue);

    if (isNaN(numericValue)) {
      setError(true);
      setHelperText(t(i18n)`Please enter a valid number`);
      return;
    }

    if (numericValue < minAmount) {
      setError(true);
      setHelperText(t(i18n)`Price cannot be below ${minAmount}`);
    } else if (numericValue > maxAmount) {
      setError(true);
      setHelperText(t(i18n)`Price cannot be higher than ${maxAmount}`);
    } else {
      setError(false);
      setHelperText("");
    }

    if (ownAmountOption) {
      setInputValues(ownAmountOption.product_option_id, numericValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (["e", "E", "+", "-"].includes(event.key)) {
      event.preventDefault();
    }
  };

  const productOptionId = ownAmountOption?.product_option_id;

  return (
    <InputGrayStart
      placeholder={t(i18n)`Enter amount`}
      name="own"
      type="number"
      min={0}
      value={ownAmount}
      onKeyDown={handleKeyDown}
      onChange={handleAmountChange}
      error={productOptionId ? !!apiErrors?.[productOptionId] : false}
      helperText={productOptionId ? apiErrors?.[productOptionId] || helperText : helperText}
      startAdornmentLabel="CHF"
      maxWidth="260px"
      sxProps={{ mt: 1 }}
    />
  );
};

export default React.memo(OwnAmountInput);
